import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Tab, FooterButton } from 'Core/components';
import PropTypes from 'prop-types';
import InventorySerialNumberTable from './inventory-serialnumber-table';

import { InventoryService } from '../../service/api/inventory-service';

const InventoryDetailModal = ({ show, onHide, inventoryData, highlightSerialNumber }) => {
  const [locationData, setLocationData] = useState([]);
  const [lotData, setLotData] = useState([]);
  const [logData, setLogData] = useState([]);
  const [serialNumbersData, setSerialNumbersData] = useState([]);
  const [serialNumbersLoading, setSerialNumbersLoading] = useState(false);
  const [generalInfo, setGeneralInfo] = useState(null);
  const [dateFilter, setDateFilter] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [inventoryDetailsFetched, setInventoryDetailsFetched] = useState(false);
  const [serialPageNumber, setSerialPageNumber] = useState(1);
  const [serialPageSize, setSerialPageSize] = useState(10);
  const [serialTotalCount, setSerialTotalCount] = useState(0);



  // Function to fetch inventory details (for Production type)
  const fetchInventoryDetails = async (partId, pageNumber = 1, pageSize = 10) => {
    try {

      setSerialNumbersLoading(true);
      setGeneralInfo(null);
      setLocationData([]);
      setLotData([]);
      setLogData([]);
      const response = await InventoryService.getInventoryDetails(
        partId,
        pageNumber,
        pageSize,
        highlightSerialNumber,
      );
      if (response && response.status === 200 && response.response) {
        // Set general info - map to consistent field names
        if (response.response.generalInfo) {
          const general = response.response.generalInfo;
          setGeneralInfo({
            PartNumber: general.partNumber || '',
            PartClass: general.partClass || '',
            partDescription: general.partDescription || '',
            OnHandQuantity: general.onHandQuantity || 0,
            OnHand: general.onHand || '',
            InventoryDate: general.inventoryDate || '',
            InventoryScan: general.inventoryScan || '',
            InventoryScanDate: general.inventoryScanDate || '',
            IQMSInventory: general.iqmsInventory || '',
            IQMSInventoryDate: general.iqmsInventoryDate || '',
            Pallet: general.pallet || '',
            IsQuarantine: general.isQuarantine || '',
            UpdateOnHand: general.updateOnHand || '',
            CreatedBy: general.createdBy || '',
            ScannedBy: general.scannedBy || '',
            InventoryType: general.inventoryType || '',
            Status: general.status || '',
            LocationId: general.locationId || null,
            LocationName: general.locationName || '',
            FromDate: general.fromDate || '',
            ToDate: general.toDate || '',
            ProcessStatus: general.processStatus || '',
            DisplayStatus: general.displayStatus || '',
            BackgroundColor: general.backgroundColor || '',
            TextColor: general.textColor || '',
            MachineId: general.machineId || null,
            MachineName: general.machineName || '',
            ProcessComments: general.processComments || ''
          });
        }
        
        // Set serial numbers data (backend handles highlight ordering)
        if (response.response.serialNumbers && response.response.serialNumbers.length > 0) {
          setSerialNumbersData(response.response.serialNumbers);
        } else {
          setSerialNumbersData([]);
        }

        if (typeof response.response.serialNumbersTotalCount === 'number') {
          setSerialTotalCount(response.response.serialNumbersTotalCount);
        } else {
          // Fallback if backend doesn't return total yet
          setSerialTotalCount(response.response.serialNumbers ? response.response.serialNumbers.length : 0);
        }
        
        // Set location data - now comes pre-aggregated from stored procedure
        if (response.response.locationInfo && response.response.locationInfo.length > 0) {
          setLocationData(response.response.locationInfo.map(loc => ({
            locationName: loc.locationName || 'Unknown Location',
            description: loc.locationDescription || 'No description available',
            quantity: loc.quantity || '0'
          })));
        } else {
          setLocationData([]);
        }
        
        // Set lot data for Production inventory
        if (response.response.lotNumbers && response.response.lotNumbers.length > 0) {
          console.log('🔍 DEBUG - Production lot numbers found:', response.response.lotNumbers);
          setLotData(response.response.lotNumbers.map(lot => ({
            lotNumber: lot.lotNumber || 'N/A',
            quantity: lot.quantity || '0',
            inventoryDate: lot.inventoryDate || 'N/A',
            poNumber: lot.poNumber || 'N/A',
            shipmentNumber: lot.shipmentNumber || 'N/A',
            vendorName: lot.vendorName || 'N/A',
            createdBy: lot.createdBy || 'N/A'
          })));
        } else {
          console.log('🔍 DEBUG - No lot numbers in Production response');
          setLotData([]);
        }
        
        // Set log data
        if (response.response.logEntries && response.response.logEntries.length > 0) {
          console.log('🔍 DEBUG - Log entries found:', response.response.logEntries);
          setLogData(response.response.logEntries.map(log => ({
            date: log.date || 'N/A',
            inOut: log.inOut || 'N/A',
            transactionType: log.transactionType || 'N/A',
            transQty: log.transQty || '0',
            startQty: log.startQty || '0',
            endQty: log.endQty || '0',
            locationName: log.locationName || 'N/A',
            lotNumber: log.lotNumber || 'N/A',
            shipmentNumber: log.shipmentNumber || 'N/A',
            poNumber: log.poNumber || 'N/A',
            reason: log.reason || 'N/A',
            userName: log.userName || 'N/A',
            createdDate: log.createdDate || 'N/A'
          })));
          console.log('🔍 DEBUG - Mapped log data:', logData);
        } else {
          console.log('🔍 DEBUG - No log entries in response');
          setLogData([]);
        }
        
        return true;
      } else {
        setSerialNumbersData([]);
        setLotData([]);
        setLocationData([]);
        setLogData([]); 
        setGeneralInfo(null);
        setSerialTotalCount(0);
        return false;
      }
    } catch (error) {
      console.error('Error fetching process details:', error);
      setSerialNumbersData([]);
      setLotData([]);
      setLocationData([]);
      setLogData([]);
      setGeneralInfo(null);
      setSerialTotalCount(0);
      return false;
    } finally {
      setSerialNumbersLoading(false);
    }
  };

  // Function to fetch receiving inventory details (for In House type)
  const fetchReceivingInventoryDetails = async (partId) => {
    try {

      setSerialNumbersLoading(true);
      setGeneralInfo(null);
      setLocationData([]);
      setLotData([]);
      setLogData([]);
      const response = await InventoryService.getReceivingInventoryDetails(partId);
      if (response && response.status === 200 && response.response) {
        // Set general info - map to same field names as Production data
        if (response.response.generalInfo) {
          const general = response.response.generalInfo;
          setGeneralInfo({
            PartNumber: general.partNumber || '',
            PartClass: general.partClass || '',
            partDescription: general.partDescription || '',
            OnHandQuantity: general.onHandQuantity || 0,
            OnHand: general.onHand || '',
            InventoryDate: general.inventoryDate || '',
            InventoryScan: general.inventoryScan || '',
            InventoryScanDate: general.inventoryScanDate || '',
            IQMSInventory: general.iqmsInventory || '',
            IQMSInventoryDate: general.iqmsInventoryDate || '',
            Pallet: general.pallet || '',
            IsQuarantine: general.isQuarantine || '',
            UpdateOnHand: general.updateOnHand || '',
            CreatedBy: general.createdBy || '',
            ScannedBy: general.scannedBy || '',
            InventoryType: general.inventoryType || '',
            Status: general.status || '',
            POId: general.poId || null,
            ReceivingShipmentId: general.receivingShipmentId || null,
            Operator: general.operator || '',
            PONumber: general.poNumber || '',
            ShipmentNumber: general.shipmentNumber || '',
            VendorName: general.vendorName || ''
          });
        }
        
        // Set serial numbers data
        if (response.response.serialNumbers && response.response.serialNumbers.length > 0) {
          setSerialNumbersData(response.response.serialNumbers);
        } else {
          setSerialNumbersData([]);
        }
        
        // Set location data - now comes pre-aggregated from stored procedure
        if (response.response.locationInfo && response.response.locationInfo.length > 0) {
          setLocationData(response.response.locationInfo.map(loc => ({
            locationName: loc.locationName || 'Unknown Location',
            description: loc.locationDescription || 'No description available',
            quantity: loc.quantity || '0'
          })));
        } else {
          setLocationData([]);
        }
        
        // Set lot data
        if (response.response.lotNumbers && response.response.lotNumbers.length > 0) {
          setLotData(response.response.lotNumbers.map(lot => ({
            lotNumber: lot.lotNumber || 'N/A',
            quantity: lot.quantity || '0',
            inventoryDate: lot.inventoryDate || 'N/A',
            poNumber: lot.poNumber || 'N/A',
            shipmentNumber: lot.shipmentNumber || 'N/A',
            vendorName: lot.vendorName || 'N/A',
            createdBy: lot.createdBy || 'N/A'
          })));
        } else {
          setLotData([]);
        }
        
        // Set log data for receiving inventory
        if (response.response.logEntries && response.response.logEntries.length > 0) {
          setLogData(response.response.logEntries.map(log => ({
            date: log.date || 'N/A',
            inOut: log.inOut || 'N/A',
            transactionType: log.transactionType || 'N/A',
            transQty: log.transQty || '0',
            startQty: log.startQty || '0',
            endQty: log.endQty || '0',
            locationName: log.locationName || 'N/A',
            lotNumber: log.lotNumber || 'N/A',
            shipmentNumber: log.shipmentNumber || 'N/A',
            poNumber: log.poNumber || 'N/A',
            reason: log.reason || 'N/A',
            userName: log.userName || 'N/A',
            createdDate: log.createdDate || 'N/A'
          })));
        } else {
          setLogData([]);
        }
        
      } else {
        setSerialNumbersData([]);
        setLocationData([]);
        setLotData([]);
        setLogData([]);
        return false;
      }
    } catch (error) {
      console.error('Error fetching receiving inventory details:', error);
      setSerialNumbersData([]);
      setLocationData([]);
      setLotData([]);
      setLogData([]);
      setGeneralInfo(null);
      return false;
    } finally {
      setSerialNumbersLoading(false);
    }
  };

  useEffect(() => {
    // Reset modal state when we open it for a different inventory row.
    if (show && inventoryData) {
      setActiveTabIndex(0);
      setInventoryDetailsFetched(false);
      setGeneralInfo(null);
      setSerialNumbersData([]);
      setLocationData([]);
      setLotData([]);
      setLogData([]);
      setSerialPageNumber(1);
      setSerialPageSize(10);
      setSerialTotalCount(0);
    }

    // Reset filters when inventory data changes / modal opens
    setDateFilter('');
    setShowDateFilter(false);
  }, [show, inventoryData, highlightSerialNumber]);

  const handleTabChange = (index) => {
    setActiveTabIndex(index);

    // Lazy-load inventory details only when user clicks "Serial Numbers" tab.
    // Tab indices: 0=General, 1=Serial Numbers, 2=Lot Information, 3=Location, 4=Log
    if (index !== 1) return;
    if (inventoryDetailsFetched) return;
    if (!inventoryData?.partId) return;
    if (serialNumbersLoading) return;

    const inventoryType = inventoryData.inventoryType || inventoryData.InventoryType || inventoryData.type || '';

    (async () => {
      let success = false;
      if (inventoryType.toLowerCase().includes('in house') || inventoryType.toLowerCase().includes('inhouse')) {
        // If you also add paging backend for receiving, update this call similarly.
        success = await fetchReceivingInventoryDetails(inventoryData.partId);
      } else {
        success = await fetchInventoryDetails(inventoryData.partId, serialPageNumber, serialPageSize);
      }
      if (success) setInventoryDetailsFetched(true);
    })();
  };

  const handleSerialPageChange = (pageNumber, pageSize) => {
    setSerialPageNumber(pageNumber);
    setSerialPageSize(pageSize);

    if (!inventoryData?.partId) return;
    if (serialNumbersLoading) return;

    const inventoryType = inventoryData.inventoryType || inventoryData.InventoryType || inventoryData.type || '';
    if (inventoryType.toLowerCase().includes('in house') || inventoryType.toLowerCase().includes('inhouse')) {
      // Not wired for paging yet; leave as-is.
      return;
    }

    fetchInventoryDetails(inventoryData.partId, pageNumber, pageSize);
  };

  // Close date filter when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateFilter && !event.target.closest('th[style*="position: relative"]')) {
        setShowDateFilter(false);
      }
    };

    if (showDateFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDateFilter]);



  const renderGeneralTab = () => (
    <div className="p-4">
      <div className="row">
        <div className="col-md-3">
          <div className="mb-3">
            <label className="form-label fw-bold">Part Number*:</label>
            <input 
              type="text" 
              className="form-control" 
              value={generalInfo?.PartNumber || inventoryData?.partNumber || ''} 
              readOnly
            />
          </div>
        </div>

        <div className="col-md-3">
          <div className="mb-3">
            <label className="form-label fw-bold">Part Class:</label>
            <input 
              type="text" 
              className="form-control" 
              value={generalInfo?.PartClass || inventoryData?.class || ''} 
              readOnly
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="mb-3">
            <label className="form-label fw-bold">Part Description:</label>
            <input 
              type="text" 
              className="form-control" 
              value={generalInfo?.partDescription || ''} 
              readOnly
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="mb-3">
            <label className="form-label fw-bold">IQMS OnHand:</label>
            <input 
              type="text" 
              className="form-control" 
              value={generalInfo?.OnHandQuantity || ''} 
              readOnly
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="mb-3">
            <label className="form-label fw-bold">OnHand:</label>
            <input 
              type="text" 
              className="form-control" 
              value={generalInfo?.OnHand || ''} 
              readOnly
            />
          </div>
        </div>

      </div>
    </div>
  );

  const renderSerialNumbersTab = () => (
    <div className="p-4">
      {serialNumbersLoading ? (
        <div className="text-center text-muted py-4">
          <p>Loading serial numbers...</p>
        </div>
      ) : serialNumbersData && serialNumbersData.length > 0 ? (
        <InventorySerialNumberTable
          data={serialNumbersData}
          pagination={{
            current: serialPageNumber,
            pageSize: serialPageSize,
            total: serialTotalCount,
          }}
          onPageChange={handleSerialPageChange}
        />
      ) : (
        <div className="text-center text-muted py-4">
          <p>No serial numbers found for this inventory item.</p>
        </div>
      )}
    </div>
  );

  const renderLotInformationTab = () => {
    // Check if this is Production inventory type
    const isProductionType = inventoryData?.inventoryType?.toLowerCase().includes('production') || 
                           inventoryData?.inventoryType?.toLowerCase().includes('in production');
    
    return (
      <div className="p-4">
        {serialNumbersLoading ? (
          <div className="text-center text-muted py-4">
            <p>Loading lot information...</p>
          </div>
        ) : lotData && lotData.length > 0 ? (
          <div>
            <h6 className="mb-3">Lot Numbers Information</h6>
            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead className="table-dark">
                  <tr>
                    <th>Lot Number</th>
                    <th>Quantity</th>
                    {!isProductionType && (
                      <>
                        <th>Inventory Date</th>
                        <th>PO Number</th>
                        <th>Shipment Number</th>
                        <th>Vendor Name</th>
                        <th>Created By</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {lotData.map((lot, index) => (
                    <tr key={index}>
                      <td className="fw-bold">{lot.lotNumber}</td>
                      <td>{lot.quantity}</td>
                      {!isProductionType && (
                        <>
                          <td>{lot.inventoryDate}</td>
                          <td>{lot.poNumber}</td>
                          <td>{lot.shipmentNumber}</td>
                          <td>{lot.vendorName}</td>
                          <td>{lot.createdBy || 'N/A'}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted py-4">
            <p>No lot numbers found for this inventory item.</p>
          </div>
        )}
      </div>
    );
  };

  const renderLocationTab = () => (
    <div className="p-4">
      {serialNumbersLoading ? (
        <div className="text-center text-muted py-4">
          <p>Loading location information...</p>
        </div>
      ) : locationData && locationData.length > 0 ? (
        <div>
          <h6 className="mb-3">Location Information</h6>
          <div className="table-responsive">
            <table className="table table-striped table-bordered">
              <thead className="table-dark">
                <tr>
                  <th>Location Name</th>
                  <th>Description</th>
                  <th>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {locationData.map((location, index) => (
                  <tr key={index}>
                    <td className="fw-bold">{location.locationName}</td>
                    <td>{location.description}</td>
                    <td>{location.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center text-muted py-4">
          <p>No location information available.</p>
        </div>
      )}
    </div>
  );

    const renderLogTab = () => {
      // Filter log data by date if dateFilter is set
      const filteredLogData = dateFilter
        ? logData.filter(log => {
            if (!log.date) return false;
            // Convert log date to YYYY-MM-DD format for comparison
            const logDate = new Date(log.date);
            const filterDate = new Date(dateFilter);
            return (
              logDate.getFullYear() === filterDate.getFullYear() &&
              logDate.getMonth() === filterDate.getMonth() &&
              logDate.getDate() === filterDate.getDate()
            );
          })
        : logData;

      return (
        <div className="p-4">
          {logData && logData.length > 0 ? (
            <div>
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th style={{ position: 'relative', cursor: 'pointer' }}>
                        <div 
                          onClick={() => setShowDateFilter(!showDateFilter)}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                          <span>Date</span>
                          <span style={{ fontSize: '12px', opacity: 0.7 }}>🔽</span>
                        </div>
                        {showDateFilter && (
                          <div 
                            style={{ 
                              position: 'absolute', 
                              top: '100%', 
                              left: 0, 
                              zIndex: 10, 
                              backgroundColor: 'white', 
                              padding: '8px', 
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              minWidth: '200px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              value={dateFilter}
                              onChange={(e) => setDateFilter(e.target.value)}
                              placeholder="Filter by date"
                            />
                            {dateFilter && (
                              <button
                                className="btn btn-sm btn-link mt-2 p-0"
                                onClick={() => {
                                  setDateFilter('');
                                  setShowDateFilter(false);
                                }}
                                style={{ fontSize: '12px' }}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        )}
                      </th>
                      <th>In/Out</th>
                      <th>Transaction Type</th>
                      <th>Quantity</th>
                      <th>Start Qty</th>
                      <th>End Qty</th>
                      <th>Location</th>
                      <th>Lot Number</th>
                      <th>Shipment</th>
                      <th>PO Number</th>
                      <th>Reason</th>
                      <th>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogData.length > 0 ? (
                      filteredLogData.map((log, index) => (
                        <tr key={index}>
                          <td>{log.date}</td>
                          <td>
                            <span className={`badge ${log.inOut === 'In' ? 'bg-success' : 'bg-danger'}`}>
                              {log.inOut}
                            </span>
                          </td>
                          <td>{log.transactionType}</td>
                          <td>{log.transQty}</td>
                          <td>{log.startQty}</td>
                          <td>{log.endQty}</td>
                          <td>{log.locationName}</td>
                          <td>{log.lotNumber}</td>
                          <td>{log.shipmentNumber}</td>
                          <td>{log.poNumber}</td>
                          <td>{log.reason}</td>
                          <td>{log.userName}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="12" className="text-center text-muted py-4">
                          No log entries found for the selected date.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {dateFilter && (
                <div className="mt-2">
                  <span className="badge bg-info">
                    Showing {filteredLogData.length} of {logData.length} entries
                  </span>
                  <button
                    className="btn btn-sm btn-link ms-2"
                    onClick={() => setDateFilter('')}
                  >
                    Clear filter
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-muted py-4">
              <p>No log entries available for this inventory item.</p>
            </div>
          )}
        </div>
      );
    };

  const body = () => {
    return (
      <div className="row">
        <Tabs activeIndex={activeTabIndex} onTabChange={handleTabChange}>
          <Tab title="General">
            {renderGeneralTab()}
          </Tab>
          <Tab title={`Serial Numbers${serialNumbersLoading ? ' (Loading...)' : ''}`}>
            {renderSerialNumbersTab()}
          </Tab>
          <Tab title="Lot Information">
            {renderLotInformationTab()}
          </Tab>
          <Tab title="Location">
            {renderLocationTab()}
          </Tab>
          <Tab title="Log">
            {renderLogTab()}
          </Tab>
        </Tabs>
      </div>
    );
  };

  const footer = () => {
    return (
      <div className="row">
        <div className="col-lg-8">
          {/* Action buttons can be added here if needed */}
        </div>
        <div className="col-lg-4">
          <FooterButton
            cancelText="Close"
            hideSave={true}
            onCancel={onHide}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      id="inventory-detail-modal"
      show={show}
      size="xl"
      dialogClassName="modal-90w"
      title={`Inventory Details - ${inventoryData?.partNumber || 'N/A'}`}
      body={body}
      footer={footer}
      handleClose={onHide}
    />
  );
};

InventoryDetailModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  inventoryData: PropTypes.object,
  highlightSerialNumber: PropTypes.string,
};

export default InventoryDetailModal;
